'use client';

import { getBrowserFfmpegRuntime } from '@/lib/browser-gif-tools';
import { clamp, getBaseName } from '@/lib/browser-image-tools';
import { createZipBlob } from '@/lib/browser-png-tools';

type FfmpegRuntime = Awaited<ReturnType<typeof getBrowserFfmpegRuntime>>;

export type MediaFileKind = 'audio' | 'video';

export type MediaFileMetadata = {
  durationMs: number;
  size: number;
  mimeType: string;
  approximateBitrateKbps: number | null;
};

export type AudioSegmentDefinition = {
  startMs: number;
  endMs: number;
};

export type AudioSegmentExport = AudioSegmentDefinition & {
  index: number;
  name: string;
  blob: Blob;
};

export type AudioExportResult = {
  blob: Blob;
  extension: 'mp3' | 'wav' | 'm4a' | 'aac' | 'ogg';
  mimeType: string;
};

const audioExtensionPattern = /\.(mp3|wav|m4a|aac|ogg|oga|flac|webm)$/i;
const m4aExtensionPattern = /\.(m4a|mp4|m4b)$/i;
const wavExtensionPattern = /\.(wav|wave)$/i;
const aacExtensionPattern = /\.(aac)$/i;
const oggExtensionPattern = /\.(ogg|oga)$/i;
const cuttableAudioExtensionPattern = /\.(mp3|wav|m4a|aac|ogg|oga)$/i;
// The formats a phone actually writes a voice note in, not the formats a browser can play.
// WhatsApp on Android saves .opus, older Android recorders write .amr or .3gp, iOS Voice
// Memos can export .caf, and Windows recorders still produce .wma. ffmpeg decodes every one
// of them; the <audio> element decodes almost none. See readMediaFileMetadata for how the
// gate was separated from the decoder.
const voiceNoteExtensionPattern = /\.(mp3|wav|m4a|aac|ogg|oga|webm|opus|amr|3gp|3gpp|caf|wma)$/i;
// The subset no browser reliably decodes natively, and so the only ones for which falling back
// to the ffmpeg probe can change the answer. Safari cannot open Ogg Opus either, which is why
// .opus is here despite Chrome and Firefox handling it.
const ffmpegOnlyAudioExtensionPattern = /\.(opus|amr|3gp|3gpp|caf|wma|aiff?|au|ra)$/i;
const videoExtensionPattern = /\.(mp4|m4v|mov|webm|avi|mpeg|mpg)$/i;

function normalizeFfmpegOutput(data: string | Uint8Array) {
  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function getFileExtension(fileName: string, fallback = 'bin') {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || fallback : fallback;
}

function formatSeconds(seconds: number) {
  return Math.max(0, seconds).toFixed(3);
}

function sanitizeBitrateKbps(value: number) {
  return clamp(Math.round(value) || 192, 64, 320);
}

function buildClipArgs(startMs?: number, endMs?: number) {
  if (typeof startMs !== 'number' && typeof endMs !== 'number') {
    return [] as string[];
  }

  const normalizedStart = Math.max(0, startMs ?? 0);
  const durationSeconds =
    typeof endMs === 'number' ? Math.max(0.05, (endMs - normalizedStart) / 1000) : null;
  const args = ['-ss', formatSeconds(normalizedStart / 1000)];

  if (durationSeconds !== null) {
    args.push('-t', formatSeconds(durationSeconds));
  }

  return args;
}

function buildAtempoFilter(speedFactor: number) {
  const clamped = clamp(speedFactor || 1, 0.25, 4);
  const filters: string[] = [];
  let remaining = clamped;

  while (remaining < 0.5) {
    filters.push('atempo=0.5');
    remaining /= 0.5;
  }

  while (remaining > 2) {
    filters.push('atempo=2');
    remaining /= 2;
  }

  filters.push(`atempo=${remaining.toFixed(4)}`);

  return filters.join(',');
}

async function cleanupFiles(runtime: FfmpegRuntime, fileNames: string[]) {
  await Promise.allSettled(fileNames.map((fileName) => runtime.ffmpeg.deleteFile(fileName)));
}

async function readBlobFromFile(runtime: FfmpegRuntime, fileName: string, mimeType: string) {
  const outputData = await runtime.ffmpeg.readFile(fileName);
  const outputBytes = normalizeFfmpegOutput(outputData);
  return new Blob([outputBytes], { type: mimeType });
}

function createMediaElement(kind: MediaFileKind) {
  return document.createElement(kind);
}

export function isMp3File(file: File) {
  return file.type === 'audio/mpeg' || /\.mp3$/i.test(file.name);
}

export function isM4aFile(file: File) {
  return (
    file.type === 'audio/mp4' || file.type === 'audio/x-m4a' || m4aExtensionPattern.test(file.name)
  );
}

export function isWavFile(file: File) {
  return (
    file.type === 'audio/wav' || file.type === 'audio/x-wav' || wavExtensionPattern.test(file.name)
  );
}

export function isAacFile(file: File) {
  return file.type === 'audio/aac' || aacExtensionPattern.test(file.name);
}

export function isCuttableAudioFile(file: File) {
  return (
    isMp3File(file) ||
    isM4aFile(file) ||
    isWavFile(file) ||
    isAacFile(file) ||
    file.type === 'audio/ogg' ||
    oggExtensionPattern.test(file.name) ||
    cuttableAudioExtensionPattern.test(file.name)
  );
}

export function isVoiceNoteFile(file: File) {
  return (
    isCuttableAudioFile(file) ||
    file.type === 'audio/webm' ||
    voiceNoteExtensionPattern.test(file.name)
  );
}

export function isAudioFile(file: File) {
  return file.type.startsWith('audio/') || audioExtensionPattern.test(file.name);
}

export function isVideoFile(file: File) {
  return file.type.startsWith('video/') || videoExtensionPattern.test(file.name);
}

// Ask ffmpeg what the file is when the browser refuses to say.
//
// `-i` with no output exits non-zero and prints the stream table to the log, which is exactly
// what we want and the reason the exit code is ignored here. Mirrors probeVideoMetadataWithFfmpeg
// in browser-video-tools.ts, which fixed the same class of bug for WMV, FLV, TS and MPEG.
async function probeAudioMetadataWithFfmpeg(file: File): Promise<MediaFileMetadata> {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `probe.${getFileExtension(file.name, 'bin')}`;
  let log = '';
  const collect = ({ message }: { message: string }) => {
    log += `${message}\n`;
  };

  try {
    runtime.ffmpeg.on('log', collect);
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(file));
    await runtime.ffmpeg.exec(['-i', inputName]);

    // An audio stream line is the proof the file is readable at all; without one there is
    // nothing to convert and the caller should see a real error rather than a zero-length clip.
    if (!/Stream #\d+:\d+.*:\s*Audio:/i.test(log)) {
      throw new Error('This audio file could not be opened.');
    }

    const duration = /Duration:\s(\d+):(\d{2}):(\d{2})\.(\d{2})/.exec(log);
    const durationMs = duration
      ? (Number(duration[1]) * 3600 + Number(duration[2]) * 60 + Number(duration[3])) * 1000 +
        Number(duration[4]) * 10
      : 0;

    return {
      durationMs,
      size: file.size,
      mimeType: file.type,
      approximateBitrateKbps:
        durationMs > 0
          ? Math.max(1, Math.round((file.size * 8) / (durationMs / 1000) / 1000))
          : null,
    };
  } finally {
    runtime.ffmpeg.off('log', collect);
    await cleanupFiles(runtime, [inputName]);
  }
}

// The media element is tried first because it is instant and needs no wasm. It was also the
// reason .opus, .amr, .3gp, .caf and .wma were rejected before ffmpeg ever saw them: the gate
// was the browser's decoder, not ours, and ffmpeg handles all of them. A WhatsApp voice note
// on Android is literally a .opus file, so the single most common input to the voice-note
// converter was the one the picker refused. Falling back costs one wasm load, and only for the
// formats the browser cannot read anyway.
export async function readMediaFileMetadata(
  file: File,
  kind: MediaFileKind,
): Promise<MediaFileMetadata> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const element = await new Promise<HTMLAudioElement | HTMLVideoElement | null>(
      (resolve, reject) => {
        const media = createMediaElement(kind) as HTMLAudioElement | HTMLVideoElement;
        media.preload = 'metadata';
        media.onloadedmetadata = () => resolve(media);
        media.onerror = () => reject(new Error('BROWSER_CANNOT_DECODE'));
        media.src = objectUrl;
      },
    ).catch((error: unknown) => {
      if (error instanceof Error && error.message === 'BROWSER_CANNOT_DECODE') {
        return null;
      }

      throw error;
    });

    if (!element) {
      if (kind === 'video') {
        throw new Error('This video file could not be opened.');
      }

      // Only the formats a browser genuinely cannot open are worth a 32 MB core download. If
      // an MP3 or a WAV fails the element it is corrupt, and the old instant error is a better
      // answer than a long wait for the same verdict.
      if (!ffmpegOnlyAudioExtensionPattern.test(file.name)) {
        throw new Error('This audio file could not be opened.');
      }

      return await probeAudioMetadataWithFfmpeg(file);
    }

    const durationSeconds = Number.isFinite(element.duration) ? element.duration : 0;
    const approximateBitrateKbps =
      durationSeconds > 0
        ? Math.max(1, Math.round((file.size * 8) / durationSeconds / 1000))
        : null;

    return {
      durationMs: Math.max(0, Math.round(durationSeconds * 1000)),
      size: file.size,
      mimeType: file.type,
      approximateBitrateKbps,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function exportAudioFromSource(params: {
  file: File | Blob;
  fileName: string;
  startMs?: number;
  endMs?: number;
  audioFilter?: string | null;
  outputName: string;
  outputMimeType: string;
  outputArgs: string[];
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.fileName, 'bin')}`;
  const cleanupTargets = [inputName, params.outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));

    const command = [...buildClipArgs(params.startMs, params.endMs), '-i', inputName, '-vn'];

    if (params.audioFilter) {
      command.push('-af', params.audioFilter);
    }

    command.push(...params.outputArgs, params.outputName);

    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The audio export could not be generated locally.');
    }

    return await readBlobFromFile(runtime, params.outputName, params.outputMimeType);
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

async function exportMp3FromSource(params: {
  file: File | Blob;
  fileName: string;
  bitrateKbps: number;
  startMs?: number;
  endMs?: number;
  audioFilter?: string | null;
}) {
  return exportAudioFromSource({
    ...params,
    outputName: 'output.mp3',
    outputMimeType: 'audio/mpeg',
    outputArgs: ['-c:a', 'libmp3lame', '-b:a', `${sanitizeBitrateKbps(params.bitrateKbps)}k`],
  });
}

function getOriginalFormatExportConfig(
  fileName: string,
  bitrateKbps: number,
): Omit<AudioExportResult, 'blob'> & {
  outputName: string;
  outputArgs: string[];
} {
  const extension = getFileExtension(fileName, 'bin');
  const normalizedBitrate = `${sanitizeBitrateKbps(bitrateKbps)}k`;

  switch (extension) {
    case 'mp3':
      return {
        extension: 'mp3',
        mimeType: 'audio/mpeg',
        outputName: 'output.mp3',
        outputArgs: ['-c:a', 'libmp3lame', '-b:a', normalizedBitrate],
      };
    case 'wav':
    case 'wave':
      return {
        extension: 'wav',
        mimeType: 'audio/wav',
        outputName: 'output.wav',
        outputArgs: ['-acodec', 'pcm_s16le'],
      };
    case 'm4a':
    case 'mp4':
    case 'm4b':
      return {
        extension: 'm4a',
        mimeType: 'audio/mp4',
        outputName: 'output.m4a',
        outputArgs: ['-c:a', 'aac', '-b:a', normalizedBitrate, '-movflags', '+faststart'],
      };
    case 'aac':
      return {
        extension: 'aac',
        mimeType: 'audio/aac',
        outputName: 'output.aac',
        outputArgs: ['-c:a', 'aac', '-b:a', normalizedBitrate, '-f', 'adts'],
      };
    case 'ogg':
    case 'oga':
      return {
        extension: 'ogg',
        mimeType: 'audio/ogg',
        outputName: 'output.ogg',
        outputArgs: ['-c:a', 'libvorbis', '-b:a', normalizedBitrate],
      };
    default:
      throw new Error('Original-format export is not available for this audio file type.');
  }
}

export async function convertAudioToMp3(params: { file: File; bitrateKbps: number }) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
  });
}

export type AudioOutputFormat = 'mp3' | 'wav' | 'm4a' | 'aac' | 'ogg' | 'flac';

function getTargetFormatExportConfig(
  format: AudioOutputFormat,
  bitrateKbps: number,
): { outputName: string; outputMimeType: string; outputArgs: string[] } {
  const normalizedBitrate = `${sanitizeBitrateKbps(bitrateKbps)}k`;

  switch (format) {
    case 'mp3':
      return {
        outputName: 'output.mp3',
        outputMimeType: 'audio/mpeg',
        outputArgs: ['-c:a', 'libmp3lame', '-b:a', normalizedBitrate],
      };
    case 'wav':
      return {
        outputName: 'output.wav',
        outputMimeType: 'audio/wav',
        outputArgs: ['-acodec', 'pcm_s16le'],
      };
    case 'm4a':
      return {
        outputName: 'output.m4a',
        outputMimeType: 'audio/mp4',
        outputArgs: ['-c:a', 'aac', '-b:a', normalizedBitrate, '-movflags', '+faststart'],
      };
    case 'aac':
      return {
        outputName: 'output.aac',
        outputMimeType: 'audio/aac',
        outputArgs: ['-c:a', 'aac', '-b:a', normalizedBitrate, '-f', 'adts'],
      };
    case 'ogg':
      return {
        outputName: 'output.ogg',
        outputMimeType: 'audio/ogg',
        outputArgs: ['-c:a', 'libvorbis', '-b:a', normalizedBitrate],
      };
    case 'flac':
      // Lossless: no bitrate. Converting from a lossy source keeps its quality, not more.
      return {
        outputName: 'output.flac',
        outputMimeType: 'audio/flac',
        outputArgs: ['-c:a', 'flac'],
      };
  }
}

export async function convertAudioToFormat(params: {
  file: File;
  targetFormat: AudioOutputFormat;
  bitrateKbps: number;
}) {
  const config = getTargetFormatExportConfig(params.targetFormat, params.bitrateKbps);
  return exportAudioFromSource({
    file: params.file,
    fileName: params.file.name,
    outputName: config.outputName,
    outputMimeType: config.outputMimeType,
    outputArgs: config.outputArgs,
  });
}

export async function trimMp3(params: {
  file: File;
  startMs: number;
  endMs: number;
  bitrateKbps: number;
}) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    startMs: params.startMs,
    endMs: params.endMs,
  });
}

export async function trimAudioToMp3(params: {
  file: File;
  startMs: number;
  endMs: number;
  bitrateKbps: number;
}) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    startMs: params.startMs,
    endMs: params.endMs,
  });
}

export async function trimAudioToOriginalFormat(params: {
  file: File;
  startMs: number;
  endMs: number;
  bitrateKbps: number;
}): Promise<AudioExportResult> {
  const exportConfig = getOriginalFormatExportConfig(params.file.name, params.bitrateKbps);
  const blob = await exportAudioFromSource({
    file: params.file,
    fileName: params.file.name,
    startMs: params.startMs,
    endMs: params.endMs,
    outputName: exportConfig.outputName,
    outputMimeType: exportConfig.mimeType,
    outputArgs: exportConfig.outputArgs,
  });

  return {
    blob,
    extension: exportConfig.extension,
    mimeType: exportConfig.mimeType,
  };
}

export async function convertM4aToMp3(params: { file: File; bitrateKbps: number }) {
  return convertAudioToMp3(params);
}

export async function convertWavToMp3(params: { file: File; bitrateKbps: number }) {
  return convertAudioToMp3(params);
}

export async function convertAacToMp3(params: { file: File; bitrateKbps: number }) {
  return convertAudioToMp3(params);
}

// Merges N audio files into one MP3.
//
// The mp3-merger variant only ever sees real MP3s, all sharing one codec, so the original
// implementation named every input "input-NNN.mp3" and used the `-f concat` demuxer, which
// stitches packets end to end under the assumption that every segment already shares the same
// codec and timebase. Once voice-memo-joiner made mixed-codec input possible (an .opus note
// followed by a .m4a one), that assumption breaks: the concat demuxer does not decode, so joining
// two different codecs through it produces silently corrupt or truncated audio rather than a
// clear error. The fix is the concat FILTER instead: each input is opened as its own `-i` with
// its own real extension, decoded by whichever codec it actually is, and the decoded PCM streams
// are concatenated before a single re-encode to MP3. Slower for the all-MP3 case, correct for
// every case.
export async function mergeMp3Files(params: { files: File[]; bitrateKbps: number }) {
  const runtime = await getBrowserFfmpegRuntime();
  const outputName = 'merged.mp3';
  const inputNames = params.files.map(
    (file, index) => `input-${String(index + 1).padStart(3, '0')}.${getFileExtension(file.name)}`,
  );
  const cleanupTargets = [outputName, ...inputNames];

  try {
    for (let index = 0; index < params.files.length; index += 1) {
      await runtime.ffmpeg.writeFile(
        inputNames[index],
        await runtime.fetchFile(params.files[index]),
      );
    }

    const inputArgs = inputNames.flatMap((name) => ['-i', name]);
    const streamLabels = inputNames.map((_, index) => `[${index}:a]`).join('');
    const filter = `${streamLabels}concat=n=${inputNames.length}:v=0:a=1[out]`;

    const exitCode = await runtime.ffmpeg.exec([
      ...inputArgs,
      '-filter_complex',
      filter,
      '-map',
      '[out]',
      '-c:a',
      'libmp3lame',
      '-b:a',
      `${sanitizeBitrateKbps(params.bitrateKbps)}k`,
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('These files could not be merged locally.');
    }

    return await readBlobFromFile(runtime, outputName, 'audio/mpeg');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function changeMp3Volume(params: { file: File; gainDb: number; bitrateKbps: number }) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    audioFilter: `volume=${params.gainDb.toFixed(2)}dB`,
  });
}

export async function changeMp3Speed(params: {
  file: File;
  speedFactor: number;
  bitrateKbps: number;
}) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    audioFilter: buildAtempoFilter(params.speedFactor),
  });
}

export async function convertMp3ToWav(file: File) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = 'input.mp3';
  const outputName = 'output.wav';
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(file));
    const exitCode = await runtime.ffmpeg.exec([
      '-i',
      inputName,
      '-vn',
      '-acodec',
      'pcm_s16le',
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('The WAV export could not be generated locally.');
    }

    return await readBlobFromFile(runtime, outputName, 'audio/wav');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function splitMp3ByRanges(params: {
  file: File;
  ranges: AudioSegmentDefinition[];
  bitrateKbps: number;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = 'input.mp3';
  const cleanupTargets = [inputName];
  const exports: AudioSegmentExport[] = [];
  const baseName = getBaseName(params.file.name);

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));

    for (let index = 0; index < params.ranges.length; index += 1) {
      const range = params.ranges[index];
      const outputName = `segment-${String(index + 1).padStart(3, '0')}.mp3`;
      cleanupTargets.push(outputName);

      const exitCode = await runtime.ffmpeg.exec([
        ...buildClipArgs(range.startMs, range.endMs),
        '-i',
        inputName,
        '-vn',
        '-c:a',
        'libmp3lame',
        '-b:a',
        `${sanitizeBitrateKbps(params.bitrateKbps)}k`,
        outputName,
      ]);

      if (exitCode !== 0) {
        throw new Error('One of the MP3 segments could not be exported locally.');
      }

      exports.push({
        index: index + 1,
        startMs: range.startMs,
        endMs: range.endMs,
        name: `${baseName}-part-${String(index + 1).padStart(2, '0')}.mp3`,
        blob: await readBlobFromFile(runtime, outputName, 'audio/mpeg'),
      });
    }

    return exports;
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function splitMp3IntoEqualParts(params: {
  file: File;
  durationMs: number;
  parts: number;
  bitrateKbps: number;
}) {
  const partCount = clamp(Math.round(params.parts) || 2, 2, 24);
  const segmentMs = Math.max(1000, Math.floor(params.durationMs / partCount));
  const ranges: AudioSegmentDefinition[] = [];

  for (let index = 0; index < partCount; index += 1) {
    const startMs = index * segmentMs;
    const endMs =
      index === partCount - 1
        ? params.durationMs
        : Math.min(params.durationMs, startMs + segmentMs);
    ranges.push({ startMs, endMs });
  }

  return splitMp3ByRanges({
    file: params.file,
    ranges,
    bitrateKbps: params.bitrateKbps,
  });
}

export async function extractVideoAudioToMp3(params: {
  file: File;
  startMs: number;
  endMs: number;
  bitrateKbps: number;
}) {
  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    startMs: params.startMs,
    endMs: params.endMs,
  });
}

export async function applyMp3Fade(params: {
  file: File;
  durationMs: number;
  fadeInMs: number;
  fadeOutMs: number;
  bitrateKbps: number;
}) {
  const filters: string[] = [];

  if (params.fadeInMs > 0) {
    filters.push(`afade=t=in:st=0:d=${formatSeconds(params.fadeInMs / 1000)}`);
  }

  if (params.fadeOutMs > 0) {
    const fadeOutStart = Math.max(0, params.durationMs - params.fadeOutMs);
    filters.push(
      `afade=t=out:st=${formatSeconds(fadeOutStart / 1000)}:d=${formatSeconds(params.fadeOutMs / 1000)}`,
    );
  }

  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    audioFilter: filters.join(','),
  });
}

export async function makeRingtoneMp3(params: {
  file: File;
  startMs: number;
  endMs: number;
  fadeInMs: number;
  fadeOutMs: number;
  gainDb: number;
  bitrateKbps: number;
}) {
  const clipDurationMs = Math.max(0, params.endMs - params.startMs);
  const filters: string[] = [`volume=${params.gainDb.toFixed(2)}dB`];

  if (params.fadeInMs > 0) {
    filters.push(`afade=t=in:st=0:d=${formatSeconds(params.fadeInMs / 1000)}`);
  }

  if (params.fadeOutMs > 0) {
    const fadeOutStart = Math.max(0, clipDurationMs - params.fadeOutMs);
    filters.push(
      `afade=t=out:st=${formatSeconds(fadeOutStart / 1000)}:d=${formatSeconds(params.fadeOutMs / 1000)}`,
    );
  }

  return exportMp3FromSource({
    file: params.file,
    fileName: params.file.name,
    bitrateKbps: params.bitrateKbps,
    startMs: params.startMs,
    endMs: params.endMs,
    audioFilter: filters.join(','),
  });
}

export async function transcodeRecordedAudioToMp3(params: {
  blob: Blob;
  fileName: string;
  bitrateKbps: number;
}) {
  return exportMp3FromSource({
    file: params.blob,
    fileName: params.fileName,
    bitrateKbps: params.bitrateKbps,
  });
}

export async function createAudioSegmentZip(segments: AudioSegmentExport[]) {
  return createZipBlob(
    segments.map((segment) => ({
      name: segment.name,
      data: segment.blob,
    })),
  );
}

export { getBaseName };
